import { useCallback } from 'react';
import { useStrategy } from '../context/StrategyContext';
import { apiService } from '../api';
import type { ApiService } from '../api/index.d';
import { StrategyConfig, TradingSignal } from '../types';

export const useStrategyOperations = () => {
  const { state, dispatch } = useStrategy();

  // Update strategy configuration
  const updateConfig = useCallback((config: StrategyConfig) => {
    dispatch({ type: 'SET_CONFIG', payload: config });
  }, [dispatch]);

  // Run the strategy with current configuration
  const runStrategy = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const signals = await apiService.runStrategy({ ticker: 'AAPL' });
      dispatch({ type: 'SET_SIGNALS', payload: signals });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to run strategy. Please try again.' });
      console.error('Error running strategy:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  // Get the latest signals
  const getLatestSignals = useCallback(async (forceRefresh: boolean = false) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const signals = await (apiService as unknown as ApiService).getTradingSignals(forceRefresh);
      dispatch({ type: 'SET_SIGNALS', payload: signals });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to fetch signals. Please try again.' });
      console.error('Error fetching signals:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  return {
    config: state.config,
    signals: state.signals,
    loading: state.loading,
    error: state.error,
    updateConfig,
    runStrategy,
    getLatestSignals,
  };
}; 